import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { RemoteConfigActionsCell } from 'src/components/remote-config/remote-configs-table/RemoteConfigActionsCell'
import { Chip } from 'src/components/shared/Chip'
import type { RemoteConfig } from 'src/interfaces/remoteConfig'
import { formatDateOnly, parseUnixTimestamp } from 'src/utils/grid'
import {
  RemoteConfigTableFields,
  remoteConfigTableHeaderTitles,
  remoteConfigValueTypeTitles,
} from './remoteConfig'

interface RemoteConfigColumnsOptions {
  canManage: boolean
  onEdit(remoteConfig: RemoteConfig): void
  onDelete(remoteConfig: RemoteConfig): void
}

/** Rows arrive sorted by key (filterRemoteConfigs), so no column sorts. Callers memoize the result. */
export const buildRemoteConfigColumns = ({
  canManage,
  onEdit,
  onDelete,
}: RemoteConfigColumnsOptions): Array<GridColDef<RemoteConfig>> => {
  const columns: Array<GridColDef<RemoteConfig>> = [
    {
      field: RemoteConfigTableFields.key,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.key],
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      hideable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<RemoteConfig>) => {
        return (
          <Typography variant="subtitle1" css={tw`truncate text-shades-1000`}>
            {params.row.key}
          </Typography>
        )
      },
    },
    {
      field: RemoteConfigTableFields.type,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.type],
      width: 110,
      sortable: false,
      valueGetter: (_value: unknown, row: RemoteConfig) => {
        return remoteConfigValueTypeTitles[row.type]
      },
    },
    {
      field: RemoteConfigTableFields.value,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.value],
      flex: 2,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<RemoteConfig>) => {
        // Compact JSON reads the same for every type: a quoted string, a bare number or boolean, an object.
        return (
          <span
            css={[
              tw`truncate font-mono text-xs`,
              params.row.enabled ? tw`text-shades-1000` : tw`text-shades-500`,
            ]}
            title={JSON.stringify(params.row.value)}
          >
            {JSON.stringify(params.row.value)}
          </span>
        )
      },
    },
    {
      field: RemoteConfigTableFields.description,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.description],
      flex: 2,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<RemoteConfig>) => {
        return (
          <Typography variant="body2" css={tw`truncate`}>
            {params.row.description.trim() === '' ? '—' : params.row.description}
          </Typography>
        )
      },
    },
    {
      field: RemoteConfigTableFields.enabled,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.enabled],
      width: 100,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<RemoteConfig>) => {
        if (params.row.enabled) {
          return (
            <Chip text="On" className={tw`bg-success-200`} textClassName={tw`text-success-500`} />
          )
        }

        return <Chip text="Off" textClassName={tw`text-shades-800`} />
      },
    },
    {
      field: RemoteConfigTableFields.updatedAt,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.updatedAt],
      width: 140,
      sortable: false,
      type: 'date',
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateOnly,
    },
    {
      field: RemoteConfigTableFields.actions,
      headerName: remoteConfigTableHeaderTitles[RemoteConfigTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<RemoteConfig>) => {
        return (
          <RemoteConfigActionsCell remoteConfig={params.row} onEdit={onEdit} onDelete={onDelete} />
        )
      },
    },
  ]

  if (canManage) {
    return columns
  }

  return columns.filter((column) => {
    return column.field !== RemoteConfigTableFields.actions
  })
}
